<template>
  <div
    ref="reference"
    class="NovaTooltip"
    data-testid="nova-tooltip"
    @mouseenter="open = true"
    @mouseleave="open = false"
  >
    <slot />
  </div>

  <Teleport to="body">
    <div
      v-if="open && !disabled"
      ref="floating"
      class="z-80 relative"
      role="tooltip"
      :class="`NovaTooltip__floating--${theme}`"
      :style="floatingStyles"
    >
      <div class="NovaTooltip__bubble" data-testid="nova-tooltip-tip">
        <slot name="content" />
      </div>
      <div
        id="arrow"
        ref="floatingArrow"
        class="NovaTooltip__arrow"
        data-testid="nova-tooltip-arrow"
        :style="arrowPosition"
      ></div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { Placement, useFloating, autoUpdate, offset, arrow, Side } from "@floating-ui/vue";
import { ref } from "vue";

export interface NovaTooltipProps {
  position?: Placement;
  theme?: "light" | "dark";
  disabled?: boolean;
}

const props = withDefaults(defineProps<NovaTooltipProps>(), {
  position: "top",
  theme: "light",
  disabled: false,
});

const open = ref(false);
const reference = ref();
const floating = ref();
const floatingArrow = ref();

const middleware = ref([offset(10), arrow({ element: floatingArrow })]);

const arrowPosition = computed(() => {
  if (!middlewareData.value.arrow) {
    return {};
  }

  const { x, y } = middlewareData.value.arrow;
  const side = props.position.split("-")[0] as Side;

  const positionMap = {
    top: { left: `${x}px`, bottom: `-${floatingArrow.value?.offsetHeight / 2}px` },
    bottom: { left: `${x}px`, top: `-${floatingArrow.value?.offsetHeight / 2}px` },
    left: { top: `${y}px`, right: `-${floatingArrow.value?.offsetWidth / 2}px` },
    right: { top: `${y}px`, left: `-${floatingArrow.value?.offsetWidth / 2}px` },
  };

  return positionMap[side];
});

const { floatingStyles, middlewareData } = useFloating(reference, floating, {
  placement: props.position,
  open: open.value,
  middleware,
  whileElementsMounted: autoUpdate,
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/utilities";

.NovaTooltip {
  &__floating--light {
    --background-color: white;
    --text-color: var(--color-text-100);
  }

  &__floating--dark {
    --background-color: var(--color-text-100);
    --text-color: white;
  }

  &__bubble {
    filter: drop-shadow(var(--box-shadow-popover));
    width: max-content;
    max-width: 300px;
    padding: rem(8) rem(16);
    border: 2px solid transparent;
    border-radius: var(--border-radius-default);
    color: var(--text-color);
    background-color: var(--background-color);

    @include font-semibold(12);
  }

  &__arrow {
    position: absolute;
    height: 15px;
    width: 15px;
    transform: rotate(45deg);
    background-color: var(--background-color);
  }
}
</style>
